import { useQueryClient } from '@tanstack/react-query';
import { Badge } from '@clay-run/glaze/badge';
import { Button } from '@clay-run/glaze/button';
import {
  CollapsibleCard,
  CollapsibleCardBody,
  CollapsibleCardHeader,
} from '@clay-run/glaze/collapsible-card';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { Text } from '@clay-run/glaze/text';

import { formatEventDate } from '../dates';
import { playDisplayName, playStatusBadgeStatus, playStatusLabel } from '../plays-logic';
import type { PlaybooksPlaysLibraryGroup } from '../schemas';
import { invalidatePlayLists } from '../use-plays-library';
import { usePlayRowActions } from './plays-actions';
import { EmptySectionRow } from './PlaybooksConfigPage';
import { KebabMenu } from './KebabMenu';

export interface PlaysLibrarySectionProps {
  groups: readonly PlaybooksPlaysLibraryGroup[];
  playbookNameById: ReadonlyMap<string, string>;
  basePath: string;
  hasSearch: boolean;
  /**
   * Codex fixes 2026-09-17, F1 — the row's "Open" navigation, supplied by the
   * page so it goes through `PlaybooksConfigPage`'s `guardedNavigate`
   * (QA round 7, U2 — now `UnsavedWorkGuard.tsx#useGuardedFlowEntry`, which
   * ALSO asks when opening this play would wipe a different flow's
   * surviving drafts). This section holds NO router hook of its own: an
   * imperative `navigate()` here has no anchor and no click for the guard's
   * capture-phase listener to see, so a dirty segmentation/copy draft on
   * this page was silently discarded — the third instance of the class that
   * file's doc names (`handleEdit`, post-create).
   */
  onOpen: (href: string) => void;
}

/**
 * The Library page's "Plays" section (2026-09-17 QA follow-ups, U1) — one
 * collapsible group per campaign (collapsed by default, expanded while
 * searching — QA round 7, item 8), each row: the play's display name, a
 * Draft/Sent badge, the playbook name, a created-date badge, "Open", and a
 * kebab with Rename/Delete that behave exactly like Home's
 * (`usePlayRowActions`, shared).
 *
 * No "Create Play" here — a play needs a campaign context, and Home's row is
 * where that lives.
 */
export function PlaysLibrarySection({ groups, playbookNameById, basePath, hasSearch, onOpen }: PlaysLibrarySectionProps) {
  const queryClient = useQueryClient();
  const actions = usePlayRowActions((play) => invalidatePlayLists(queryClient, play.campaignSfdcId));

  return (
    <>
      {groups.length === 0 ? (
        <EmptySectionRow hasSearch={hasSearch} kind="Plays" />
      ) : (
        <div className="flex flex-col gap-2 p-2">
          {groups.map((group) => (
            // QA round 7, item 8 — collapsed by default (Spencer); while a
            // search is active the groups open so a match is never hidden
            // behind a chevron. The `key` flips with `hasSearch` because
            // `CollapsibleCard` is uncontrolled — a `defaultOpen` change alone
            // would not reopen an already-mounted card.
            <CollapsibleCard
              key={`${group.campaignSfdcId}:${hasSearch ? 'search' : 'browse'}`}
              defaultOpen={hasSearch}
              className="border-border-secondary"
            >
              <CollapsibleCardHeader>
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <Text size="sm" weight="semibold">
                    {group.campaignName ?? group.campaignSfdcId}
                  </Text>
                  <Badge>{group.plays.length}</Badge>
                  {group.campaignName === null && (
                    <Text size="xs" variant="tertiary">
                      Campaign not in the mirror
                    </Text>
                  )}
                </div>
              </CollapsibleCardHeader>
              <CollapsibleCardBody className="divide-y divide-border-secondary">
                {group.plays.map((play) => {
                  const displayName = playDisplayName(play, playbookNameById);
                  return (
                    <div key={play.id} className="flex items-center gap-3 px-3 py-2">
                      <Text size="sm" weight="medium" className="min-w-0 flex-1 truncate">
                        {displayName}
                      </Text>
                      <StatusBadge status={playStatusBadgeStatus(play.status)}>
                        {playStatusLabel(play.status)}
                      </StatusBadge>
                      <Badge>{playbookNameById.get(play.playbookId) ?? play.playbookId}</Badge>
                      <Badge>{formatEventDate(play.createdAt) ?? ''}</Badge>
                      <span className="ml-auto flex shrink-0 items-center gap-1">
                        {/*
                          KTD 3 — no Glaze `ButtonLink`: it renders glaze's own
                          nested react-router-dom v5 `Link`, which this app's
                          real v6 `MemoryRouter`/`BrowserRouter` context does
                          not satisfy (different module instance ⇒ different
                          React context object). A plain `Button` + the page's
                          `guardedNavigate` (via `onOpen`) is the sanctioned
                          replacement — never this component's own
                          `useNavigate()` (F1). QA round 7, item 9 —
                          `primary`, which Terra's tokens render blue
                          (`index.css` maps `--color-bg-action-inverse` to
                          `blueberry-500`).
                        */}
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() =>
                            onOpen(
                              `${basePath}/play/${play.id}?campaignSfdcId=${encodeURIComponent(group.campaignSfdcId)}`,
                            )
                          }
                        >
                          Open
                        </Button>
                        <KebabMenu
                          items={actions.menuItemsFor(play, displayName)}
                          ariaLabel={`More actions for ${displayName}`}
                        />
                      </span>
                    </div>
                  );
                })}
              </CollapsibleCardBody>
            </CollapsibleCard>
          ))}
        </div>
      )}
      {/*
        Codex fixes 2026-09-17, F6 — rendered OUTSIDE the empty/rows branch:
        `usePlayRowActions`'s `renameTarget` lives on this component and
        survives a filter, but the drawer's `NameDrawerField` seeds its value
        once per mount — an early `EmptySectionRow` return unmounted it and a
        rep's half-typed name was reseeded from the original on the next
        keystroke in the search box.
      */}
      {actions.renameDrawer}
    </>
  );
}
